import BaseLoading from '@/components/base/BaseLoading';
import { Empty, Table, type TableProps } from 'antd';
import { useMemo } from 'react';
import { buildTableColumns, type BaseTableColumn } from './tableColumns';

export interface BaseTableProps<T extends object>
  extends Omit<TableProps<T>, 'columns'> {
  columns?: BaseTableColumn<T>[];
  interactiveRows?: boolean;
  /** 默认保留原生 Spin 配置；长列表可显式按当前可见正文区域反馈加载。 */
  loadingScope?: 'table' | 'visible';
  loadingLabel?: string;
  /** 超宽表格的空态可按当前可见表格宽度居中，不随全部列宽移出视口。 */
  emptyScope?: 'table' | 'visible';
}

/** 与 antd 的布尔值、Spin 配置采用同一加载判定，供组合层同步分页状态。 */
export function isTableLoading(
  loading: TableProps<object>['loading'],
): boolean {
  return typeof loading === 'boolean'
    ? loading
    : loading?.spinning ?? Boolean(loading);
}

/** 基础表格统一外观和加载/空态，保留原生列、排序、固定列及滚动契约。 */
export default function BaseTable<T extends object>({
  className,
  loading,
  locale,
  pagination = false,
  bordered = true,
  columns,
  interactiveRows = false,
  loadingScope = 'table',
  loadingLabel = '',
  emptyScope = 'table',
  ...props
}: BaseTableProps<T>) {
  const isLoading = isTableLoading(loading);
  const visibleLoading = isLoading && loadingScope === 'visible';
  const renderedColumns = useMemo(
    () => (columns ? buildTableColumns(columns) : undefined),
    [columns],
  );
  const emptyText =
    typeof locale?.emptyText === 'string' ||
    (emptyScope === 'visible' && locale?.emptyText === undefined) ? (
      <Empty
        className={
          emptyScope === 'visible'
            ? 'sticky left-4 w-[calc(100cqw_-_32px)]'
            : undefined
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={locale?.emptyText}
      />
    ) : (
      locale?.emptyText
    );
  return (
    <div
      className={`base-table relative w-full min-w-0${
        interactiveRows ? ' base-table-interactive' : ''
      }${emptyScope === 'visible' ? ' @container/base-table' : ''}`}
      aria-busy={isLoading}
    >
      <div
        inert={visibleLoading}
        className={
          visibleLoading ? 'opacity-50 pointer-events-none' : undefined
        }
      >
        <Table<T>
          {...props}
          columns={renderedColumns}
          className={className}
          loading={loadingScope === 'visible' ? false : loading}
          locale={{ ...locale, emptyText: isLoading ? null : emptyText }}
          pagination={pagination}
          bordered={bordered}
        />
      </div>
      {visibleLoading && (
        <BaseLoading
          overlay
          centerInVisibleArea
          mask={false}
          label={loadingLabel}
        />
      )}
    </div>
  );
}
